/**
 * The API client.
 *
 * Responses are `{ data }` or `{ error: { code, message } }` (§9), so this
 * unwraps the envelope and turns an error into a typed throw the UI can branch
 * on by CODE, never by message text.
 *
 * A 401 triggers ONE silent refresh and retry, then a redirect to sign-in
 * (Phase 1 step 7). One, not a loop: a token that cannot be renewed will not
 * become renewable on the third attempt, and retrying is how a sign-in loop
 * that burns Entra throttling budget gets built.
 */

import type { ErrorCode } from '@menlo/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly requestId: string | undefined;
  /**
   * The structured half of the answer. `STAGE_GATE_FAILED` carries the missing
   * fields and the stage's `entry_criteria` here, and Phase 4's board turns
   * exactly that into the inline prompt — so it must survive the throw rather
   * than being flattened into a message string.
   */
  readonly details: unknown;

  constructor(
    status: number,
    code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    requestId?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

/**
 * How the current auth mode attaches credentials. Entra supplies a bearer
 * token; the dev bypass supplies `x-dev-user`. Everything else in the client
 * is identical, which is the point — the dev path exercises the real one.
 */
export interface AuthStrategy {
  headers(): Promise<Record<string, string>>;
  /** Attempt a silent renewal. `true` means the request is worth retrying. */
  refresh(): Promise<boolean>;
  /** Give up: send the user to sign in, preserving where they were. */
  onAuthenticationLost(): void;
}

const anonymous: AuthStrategy = {
  headers: async () => ({}),
  refresh: async () => false,
  onAuthenticationLost: () => undefined,
};

let strategy: AuthStrategy = anonymous;

export function setAuthStrategy(next: AuthStrategy): void {
  strategy = next;
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function api<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (response.status === 401) {
    const renewed = await strategy.refresh();
    if (renewed) {
      const retry = await send(path, options);
      if (retry.status !== 401) return unwrap<T>(retry);
    }
    strategy.onAuthenticationLost();
    throw await toError(response);
  }

  return unwrap<T>(response);
}

/**
 * The same request, returning the raw `Response`.
 *
 * Two callers need it and both are deliberate: the CSV export, which is
 * `text/csv` rather than a `{ data }` envelope, and the attachment download,
 * which is a 302 to a presigned URL and must not be followed by `fetch`.
 * Everything else goes through `api()`.
 */
export async function apiRaw(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const response = await send(path, options);
  if (response.status === 401) {
    const renewed = await strategy.refresh();
    if (renewed) return send(path, options);
    strategy.onAuthenticationLost();
  }
  return response;
}

async function send(path: string, options: ApiRequestOptions): Promise<Response> {
  const authHeaders = await strategy.headers();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeaders,
    ...((options.headers as Record<string, string> | undefined) ?? {}),
  };

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  try {
    /**
     * **Origin-relative, always.** There is no base URL and there must not be.
     *
     * §3 puts the SPA and the API behind one distribution, so they are the same
     * origin deployed; locally Vite's proxy forwards `/api` to the Fastify
     * process, so they are the same origin there too. No code branches on the
     * environment, and nothing here is configurable — which is the point.
     *
     * A `VITE_`-prefixed base URL used to sit in front of this, sharing one
     * variable with the dev proxy's TARGET. Two purposes, one name, and the
     * prefix published it to the browser: point it at the API's own port and
     * every request becomes cross-origin, `OPTIONS /api/me` returns 404 because
     * the app declares no CORS (§3a [R4] — deliberately), and the SPA loops
     * back to sign-in with nothing naming the cause.
     */
    return await fetch(path, { ...options, headers, body });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached.');
  }
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) throw await toError(response);
  const payload = (await response.json()) as { data: T };
  return payload.data;
}

async function toError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get('x-request-id') ?? undefined;
  try {
    const payload = (await response.json()) as {
      error?: { code?: ErrorCode; message?: string; details?: unknown };
    };
    return new ApiError(
      response.status,
      payload.error?.code ?? 'INTERNAL_ERROR',
      payload.error?.message ?? 'Something went wrong.',
      requestId,
      payload.error?.details,
    );
  } catch {
    // A bare 403 from the origin-secret check (§3a) has no body at all.
    return new ApiError(response.status, 'INTERNAL_ERROR', 'Something went wrong.', requestId);
  }
}
